import React, { useState, useRef, useEffect, useContext } from 'react';
import { AccountContext } from '../../contexts/AccountContext';
import { DatePickerField } from './DatePickerField';
import { Filter } from './Filter';
import { Share } from './Share';
import { Filter as FilterType } from '../../model/Filter';
import { t } from '../../i18n';

const Header = ({ filter, loading, downloadingExcel, onChange, onDownloadPdf, onDownloadExcel, onRefresh }: Props) => {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement | null>(null);
  const params = useContext(AccountContext);

  const onChangeFilter = (locationId?: string[], locationName?: string[], installationId?: string, installationName?: string, campaignId?: string, campaignName?: string, playlistId?: string, playlistName?: string, zone?: string, gate?: string, crossingLine?: string) => {
    onChange({ locationId: locationId, locationName: locationName, installationId: installationId, installationName: installationName, campaignId: campaignId, campaignName: campaignName, playlistId: playlistId, playlistName: playlistName, zone: zone, gate: gate, crossingLine: crossingLine });
  }
  
  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target as Node)) {
        setDownloadOpen(false);
      }
    };

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  return (
    <div className="card card-body pt-3 pb-3 mt-4 mb-4">
      <div className="d-flex align-items-center">
        <div style={{ width: 320 }}>
          <DatePickerField filter={filter} onChange={onChange} />
        </div>
        <div className="flex-grow-1">
          <Filter locationId={filter.locationId} installationId={filter.installationId} campaignId={filter.campaignId} playlistId={filter.playlistId} zone={filter.zone} gate={filter.gate} crossingLine={filter.crossingLine} from={filter.from} to={filter.to} onChange={onChangeFilter} />
        </div>
        <div>
          {
            onRefresh &&
              <button type="button" className="btn btn-label-info waves-effect me-2" disabled={loading} onClick={onRefresh}>
                <i className={`ti ti-xs ti-refresh ${loading ? 'loading-icon' : ''}`} />
                <span className="ms-2 d-none d-lg-inline text-center" style={{ width: 63 }}>{loading ? t('header.loading') : t('header.refresh')}</span>
              </button>
          }
          {
            // A token link is personal and expires, so it is not shareable
            !params.token && <Share />
          }
          <div ref={downloadRef} className="dropdown d-inline-block ms-2 position-relative">
            <button
              className="btn btn-label-secondary dropdown-toggle"
              type="button"
              disabled={loading && !downloadingExcel}
              aria-haspopup="true"
              aria-expanded={downloadOpen}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDownloadOpen(open => !open);
              }}
            >
              <i className="ti ti-xs ti-download" />
              <span className="ms-2 d-none d-lg-inline">{t('header.download')}</span>
            </button>
            <ul className={`dropdown-menu dropdown-menu-end ${downloadOpen ? 'show' : ''}`} style={{ position: 'absolute' }}>
              <li>
                <button
                  type="button"
                  className={`dropdown-item d-flex align-items-center ${loading ? 'disabled' : ''}`}
                  onClick={() => {
                    setDownloadOpen(false);
                    if (!loading) {
                      onDownloadPdf();
                    }
                  }}
                >
                  {t('header.pdf')}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`dropdown-item d-flex align-items-center ${loading ? 'disabled' : ''}`}
                  onClick={() => {
                    setDownloadOpen(false);
                    if (!loading) {
                      onDownloadExcel();
                    }
                  }}
                >
                  {t('header.excel')}
                  {
                    downloadingExcel &&
                      <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
                  }
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  filter: FilterType,
  loading: boolean,
  downloadingExcel: boolean,
  onChange: (filter: FilterType) => void,
  onDownloadPdf: () => void,
  onDownloadExcel: () => void,
  onRefresh?: () => void
}

export { Header };
